// src/store/modules/org.js
import apiClient from '@/axios';
import { message } from 'ant-design-vue';
import { error } from 'jquery';

/**
 * Map Powwr meter-search response (single meter from electricMeters[0]) to EAC-like shape
 * for electric lookup modal display. Does not modify existing ElectraLink/EAC behaviour.
 */
function mapPowwrResponseToEacShape(powwrMeter) {
  if (!powwrMeter || typeof powwrMeter !== 'object') return powwrMeter;
  const d = powwrMeter;
  const loc = d.meterPointLocation || {};
  const supplier = d.currentSupplier || {};
  const profileClass = d.profileClass ?? '';
  const mtcId = d.meterTimeswitchClass ?? '';
  const lineLoss = d.lineLossFactor ?? '';
  const llfNormalized = String(lineLoss).slice(-3).padStart(3, '0').toLowerCase();
  return {
    mpan: d.mpan ?? '',
    metering_point_address_line_1: loc.addressLine1 ?? d.metering_point_address_line_1 ?? '',
    metering_point_address_line_2: loc.addressLine2 ?? d.metering_point_address_line_2 ?? '',
    metering_point_address_line_3: loc.addressLine3 ?? d.metering_point_address_line_3 ?? '',
    metering_point_address_line_4: loc.addressLine4 ?? d.metering_point_address_line_4 ?? '',
    post_code: loc.postCode ?? d.post_code ?? '',
    supplier_name: supplier.name ?? d.supplier_name ?? '',
    total_eac: d.annualConsumption ?? d.total_eac ?? '',
    line_loss_factor_class_id: lineLoss ? (lineLoss.length <= 3 ? '0' + lineLoss : lineLoss) : (d.line_loss_factor_class_id ?? ''),
    standard_settlement_configuration_id: d.standardSettlementConfiguration ?? d.standard_settlement_configuration_id ?? '',
    additional_detail: [{
      profile_class: profileClass ? String(profileClass).replace(/^0+/, '') || profileClass : (d.additional_detail?.[0]?.profile_class ?? ''),
      mtc_id: mtcId || (d.additional_detail?.[0]?.mtc_id ?? ''),
      meter_type: d.meterType ?? d.additional_detail?.[0]?.meter_type ?? '',
      measurement_class: d.measurementClass ?? d.additional_detail?.[0]?.measurement_class ?? ''
    }]
  };
}

/**
 * Map Powwr MPRN (gas) meter-search response to gas lookup modal shape.
 * Backend returns Powwr gas meter; we normalize to existing gasMprnData field names.
 */
function mapPowwrMprnToGasShape(powwrGasMeter) {
  if (!powwrGasMeter || typeof powwrGasMeter !== 'object') return powwrGasMeter;
  const d = powwrGasMeter;
  const loc = d.meterPointLocation || {};
  const supplier = d.currentSupplier || {};
  return {
    mprn: d.mprn ?? '',
    meterSerialNumber: d.serialNumber ?? d.meterSerialNumber ?? '',
    current_supplier_name: supplier.name ?? d.current_supplier_name ?? '',
    mpaq: d.annualConsumption ?? d.mpaq ?? d.consumption ?? '',
    address: [{
      primaryName: loc.addressLine1 ?? loc.buildingName ?? '',
      street1: loc.addressLine5 ?? loc.thoroughfare ?? loc.addressLine2 ?? '',
      locality1: loc.addressLine8 ?? loc.locality ?? loc.addressLine6 ?? '',
      town: loc.addressLine8 ?? loc.locality ?? '',
      postcode: loc.postCode ?? loc.postcode ?? ''
    }]
  };
}

/** Map Powwr address search (meterAddresses) to site lookup table shape. */
function mapPowwrAddressListToSiteShape(meterAddresses) {
  if (!Array.isArray(meterAddresses)) return [];
  return meterAddresses.map((item) => ({
    fullAddress: item.fullAddress ?? '',
    propertyAddressId: item.meterAddressId ?? item.propertyAddressId ?? '',
    matchedElectricity: (item.mpanCount ?? 0) > 0,
    matchedGas: (item.mprnCount ?? 0) > 0
  }));
}

/** Map one Powwr electricMeter to elecInfo shape (getCompanyDetails / electricInfos). */
function mapPowwrElectricMeterToElecInfoShape(d) {
  if (!d || typeof d !== 'object') return d;
  const loc = d.meterPointLocation || {};
  const supplier = d.currentSupplier || {};
  const pc = String(d.profileClass ?? '').padStart(2, '0');
  const mtc = String(d.meterTimeswitchClass ?? '').padStart(3, '0');
  const llf = String(d.lineLossFactor ?? '').slice(-3).padStart(3, '0').toLowerCase();
  const addressBreakdown = [
    '', '', loc.addressLine3 ?? '', '', loc.addressLine5 ?? '', '', loc.addressLine8 ?? '', loc.addressLine9 ?? '', loc.postCode ?? ''
  ];
  const meters = (d.serialNumber || d.meterType) ? [{ meter: d.serialNumber ?? '', meterType: d.meterType ?? '' }] : [];
  return {
    mpan: d.mpan ?? '',
    pc,
    mtc,
    gsp: d.gspId ?? '',
    llf,
    ssc: d.standardSettlementConfiguration ?? '',
    es: d.energisationStatus ?? '',
    currentSupplier: supplier.name ?? '',
    fullAddress: loc.fullAddress ?? '',
    addressBreakdown,
    meters,
    measurmentClass: d.measurementClass ?? ''
  };
}

/** Map one Powwr gasMeter to gasInfo shape (getCompanyDetails / gasInfos). */
function mapPowwrGasMeterToGasInfoShape(d) {
  if (!d || typeof d !== 'object') return d;
  const loc = d.meterPointLocation || {};
  const supplier = d.currentSupplier || {};
  return {
    mprn: d.mprn ?? '',
    meterSerialNumber: d.serialNumber ?? '',
    current_supplier_name: supplier.name ?? '',
    mpaq: d.annualConsumption ?? '',
    address: [{
      primaryName: loc.buildingNumber ?? loc.addressLine1 ?? '',
      street1: loc.thoroughfare ?? loc.addressLine5 ?? '',
      town: loc.locality ?? loc.addressLine8 ?? '',
      postcode: loc.postCode ?? ''
    }]
  };
}

const state = {
  suppliers: [],
  pozitiveUplifts:[],
  dropdowns: {},
  electra_link_set:false,
  lookUpData: [],
  lookUpElectricData: [],
  suppliersWithPrice: [],
  suppliersName: [],
  oppData:{}
};

const getters = {
  getSuppliers: (state) => state.suppliers,
  getPozitiveUplifts:(state) => state.pozitiveUplifts,
  getDropdowns: (state) => state.dropdowns,
  getElectraLink : (state) => state.electra_link_set,
  getlookUpData: (state) => state.lookUpData,
  getlookUpElectricData: (state) => state.lookUpElectricData,
  getsuppliersWithPrice: (state) => state.suppliersWithPrice,
  getsuppliersName: (state) => state.suppliersName,
  getoppData: (state) => state.oppData,
};

const mutations = {
  SET_SUPPLIERS(state, data) {
    state.suppliers = data;
  },
   SET_POZITIVE_UPLIFTS(state, data) {
    state.pozitiveUplifts = data;
  },
  SET_DROPDOWNS(state, data) {
    state.dropdowns = data;
  },
  SET_ELECTRA_LINK(state, data) {
    state.electra_link_set = data;
  },
  SET_LOOKUP_DATA(state, data) {
    state.lookUpData = data;
  },
  SET_LOOKUP_ELECTRIC_DATA(state, data) {
    state.lookUpElectricData = data;
  },
  SET_SUPPLIERS_PRICE_DATA(state, data) {
    state.suppliersWithPrice = data;
  },
  SET_SUPPLIERS_NAME(state, data) {
    state.suppliersName = data;
  },
  SET_OPP_DATA(state, data) {
    state.oppData = data;
  }
};

const actions = {
  async fetchSuppliers({ commit }) {
    try {
      const response = await apiClient.get('/quote/settings');
      if (response) {
        commit('SET_POZITIVE_UPLIFTS', response.data.data.pozitive_uplifts)
        commit('SET_SUPPLIERS', response.data.data.supplier);
        commit('SET_DROPDOWNS', response.data.data.dropdowns);
        commit('SET_ELECTRA_LINK', response.data.data.electra_link_set);
        return response.data.data;
      }
    } catch (error) {
      console.error('Error fetching SET_SUPPLIERS:', error);
    }
  },
  async createOpp({commit}, formData){
    try {
      const response = await apiClient.post('/docusign/create-org-opp-person', formData);
      if (response) {
        // commit('SET_LOOKUP_DATA', response.data.data.supplier); // Commit the fetched data
        return response.data; // Return the data for further use
      }
    } catch (error) {
      message.error(error.response.data.message);
      console.error('Error fetching lookup', error); // Log any errors
    }
  },
  // async fetchLookupData({ commit }, paramsData) {
  //   try {
  //     // Make a POST request with paramsData
  //     let response;
  //     const { main_production_service: main_product_service, ...restData } =paramsData;
  //     if (paramsData.search_type === "mpan") {
  //       response = await apiClient.get(
  //         `/electralink/v2/eac?mpan=${paramsData.search_value}`
  //       );
  //     } else if (
  //       paramsData.search_type === "mprn" ||
  //       (paramsData.search_type === "postcode" &&
  //         (main_product_service === "Gas" || main_product_service === "gas"))
  //     ) {
  //       response = await apiClient.post("/lookup/site", restData);
  //     } else {
  //       response = await apiClient.get(
  //         `electralink/postcodeSearch?postcode=${paramsData.search_value}`
  //       );
  //     }
  //     if (response) {
  //       commit("SET_LOOKUP_DATA", response?.data?.data?.supplier); // Commit the fetched data
  //       return response.data?.data; // Return the data for further use
  //     }
  //   } catch (error) {
  //     message.error(error.response.data.message);
  //     console.error("Error fetching lookup", error); // Log any errors
  //   }
  // },

    async fetchLookupData({ commit }, paramsData) {
    try {
      // Make a POST request with paramsData
      let response;
      const { main_production_service: main_product_service, electra_link_set:electra_link_set, ...restData } =paramsData;
      if (paramsData.search_type === "mpan" && electra_link_set) {
        response = await apiClient.get(
          `/electralink/v2/eac?mpan=${paramsData.search_value}`
        );
      } else if (
        (paramsData.search_type === "postcode" && electra_link_set && 
          (main_product_service === "Elec" || main_product_service === "elec"))
      ) {
        response = await apiClient.get(
          `electralink/postcodeSearch?postcode=${paramsData.search_value}`
        );
      } else if (paramsData.search_type === "msn") {
  const meterSerialNumber = { meterSerialNumber: paramsData.search_value };
  const type =
    main_product_service?.toLowerCase() === "elec" ? "Electricity" : "Gas";

  response = await apiClient.post(
    `/lookup/energy/${type}/search-meter-serial-number`,
    meterSerialNumber
  );
}else {
        response = await apiClient.post("/lookup/site", restData);
      }
      if (response) {
        commit("SET_LOOKUP_DATA", response?.data?.data?.supplier); // Commit the fetched data
        return response.data?.data; // Return the data for further use
      }
    } catch (error) {
      message.error(error.response.data.message);
      console.error("Error fetching lookup", error); // Log any errors
    }
  },

  async fetchElectricLookupData({ commit },paramsData) {
    try {
      // Make a POST request with paramsData
      let response;
      response = await apiClient.get(
        `/electralink/v2/eac?mpan=${paramsData.search_value}&type=${paramsData.type}`
      );
      if (response) {
        return response.data?.data; // Return the data for further use
      }
    } catch (error) {
      message.error(error.response.data.message);
      console.error("Error fetching lookup", error); // Log any errors
    }
  },

  async fetchElectricLookupStatistics({ commit }) {
    try {
      const response = await apiClient.get('/electralink/electric-lookup/statistics');
      if (response.data) {
        return response.data.data || response.data;
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Error fetching electric lookup statistics');
      console.error('Error fetching electric lookup statistics:', error);
      throw error;
    }
  },

  async fetchElectricLookupHistory({ commit }, params = {}) {
    try {
      const response = await apiClient.get('/electralink/electric-lookup/history', {
        params: {
          page: params.page || 1,
          pageSize: params.pageSize || 10,
        }
      });
      if (response.data) {
        return response.data.data || response.data;
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Error fetching electric lookup history');
      console.error('Error fetching electric lookup history:', error);
      throw error;
    }
  },

  async fetchSearchPropertyData({ commit }, paramsData) {
    try {
      // Make a POST request with paramsData
      const response = await apiClient.post(
        "/lookup/energy/property/search-by-address-id",
        paramsData
      );

      if (response) {
        commit("SET_LOOKUP_ELECTRIC_DATA", response.data);
        return response.data;
      }
    } catch (error) {
      return error.response.data;
    }
  },

  async fetchMpanAdditionalInfo({ commit }, paramsData) {
    try {
      // Make a POST request with paramsData
      const response = await apiClient.get(
        `electralink/v2/eac?mpan=${paramsData.mpan}&mpanEncrypt=true`,
        paramsData
      );

      if (response) {
        commit("SET_LOOKUP_ELECTRIC_DATA", response.data);
        return response.data;
      }
    } catch (error) {
      return error.response.data;
    }
  },

  async fetchPowwrMpanSearch({ commit }, mpan) {
    try {
      const response = await apiClient.get(
        `/powwr-lookup/search/mpan`,
        { params: { mpan } }
      );
      const data = response?.data?.data ?? response?.data ?? response;
      const rawMeter = data?.electricMeters?.[0] ?? data;
      const mapped = mapPowwrResponseToEacShape(rawMeter);
      if (response) {
        commit("SET_LOOKUP_ELECTRIC_DATA", mapped);
        return { mapped, raw: rawMeter };
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Error fetching MPAN lookup');
      console.error('Error fetching Powwr MPAN lookup', error);
      throw error;
    }
  },

  async fetchPowwrMprnSearch({ commit }, mprn) {
    try {
      const response = await apiClient.get(
        `/powwr-lookup/search/mprn`,
        { params: { mprn } }
      );
      const data = response?.data?.data ?? response?.data ?? response;
      const rawMeter = data?.gasMeters?.[0] ?? data;
      const mapped = mapPowwrMprnToGasShape(rawMeter);
      if (response) {
        return { mapped };
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Error fetching MPRN lookup');
      console.error('Error fetching Powwr MPRN lookup', error);
      throw error;
    }
  },

  async fetchPowwrAddressSearch({ commit }, postcode) {
    try {
      const response = await apiClient.get(
        `/powwr-lookup/search/address`,
        { params: { postcode } }
      );
      const data = response?.data?.data ?? response?.data ?? response;
      const list = data?.meterAddresses ?? data ?? [];
      const mapped = mapPowwrAddressListToSiteShape(list);
      if (response) {
        return mapped;
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Error fetching address search');
      console.error('Error fetching Powwr address search', error);
      throw error;
    }
  },

  async fetchPowwrMeterAddressIdSearch({ commit }, meterAddressId) {
    try {
      const response = await apiClient.get(
        `/powwr-lookup/search/meter-address-id`,
        { params: { meterAddressId } }
      );
      const data = response?.data?.data ?? response?.data ?? response;
      const electricMeters = data?.electricMeters ?? [];
      const gasMeters = data?.gasMeters ?? [];
      const elecInfo = electricMeters.map(mapPowwrElectricMeterToElecInfoShape);
      const gasInfo = gasMeters.map(mapPowwrGasMeterToGasInfoShape);
      if (response) {
        return { status: true, data: { elecInfo, gasInfo }, raw: { electricMeters, gasMeters } };
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Error fetching meter details');
      console.error('Error fetching Powwr meter-address-id', error);
      throw error;
    }
  },

  async getSupplierPrice({ commit }, formData) {

    try {
      // Make a POST request with formData
      const response = await apiClient.post('/power/pricing', formData);
      let suppliersPriceData = [];
      commit('SET_OPP_DATA', response.data.data.opportunity)
      if (response.data.data.GetGasRatesResult) {
        suppliersPriceData = response.data.data.GetGasRatesResult.Rates;
      } else if (response.data.data.GetElectricRatesResult) {
        suppliersPriceData = response.data.data.GetElectricRatesResult.Rates;
      }
      // Filter suppliers where AnnualPrice is not null or zero
      const filteredSuppliers = suppliersPriceData?.filter(supplier => supplier.AnnualPrice);
      commit('SET_SUPPLIERS_PRICE_DATA', filteredSuppliers);
      const supplierPriceData = new Set(filteredSuppliers?.map(supplier => supplier.Supplier));
      commit('SET_SUPPLIERS_NAME', supplierPriceData)
      if ((filteredSuppliers?.length === 0 || filteredSuppliers === undefined ) && response.data?.data?.GetGasRatesResult?.Error?.Message) {
        message.error(response.data.data.GetGasRatesResult.Error.ErrorDetail);
      } else if ((filteredSuppliers?.length === 0 || filteredSuppliers === undefined ) && response.data?.data?.GetElectricRatesResult?.Error?.Message) {
        message.error(response.data.data.GetElectricRatesResult.Error.ErrorDetail);
      }
      return filteredSuppliers; // Return the filtered data
    } catch (error) {
      message.error(error?.response?.data?.message);
      console.error('Error fetching lookup', error);
    }
  },
  async downloadPdf({ commit }, paramsData) {
    try {
      // Make a POST request with paramsData
      const response = await apiClient.post('power/export-price-pdf', paramsData);
      if (response.status === 200) {
        // commit('SET_LOOKUP_DATA', response.data.data.supplier); // Commit the fetched data
        return response; // Return the data for further use
      }
    } catch (error) {
      console.error('Error fetching export-price-pdf', error); // Log any errors
    }
  },
  async downloadExcel({ commit }, excelData) {
    try {
      // Make a POST request with paramsData
      const response = await apiClient.post('power/export-price-excel', excelData);
      if (response.status === 200) {
        return response; // Return the data for further use
      }
    } catch (error) {
      console.error('Error fetching export-price-excel', error); // Log any errors
    }
  },
  async downloadSupplierPdf({ commit }, params) {
    try {
      // Make a POST request with paramsData
      const response = await apiClient.post('power/contract/generate-pdf', params);
      if (response.status === 200) {
        return response.data.response; // Return the data for further use
      } else {
        message.error('Error fetching export supplier pdf');
      }
    } catch (error) {
      console.error('Error fetching export-supplier-pdf', error); // Log any errors
      return { success: false, errors: error.response.data.message };
    }
  },

  async sendSupplierPdf({ commit }, params) {
    try {
      // Make a POST request with paramsData
      const response = await apiClient.post('docusign/send', params);
      if (response.data.success) {
        if (params.opportunity_id) {
          message.success('Email send successfully');
        } else {
          message.success('Opportunity created and Email send successfully');
        }
        return response.data; // Return the data for further use
      } else {
        return { success: false, errors:response.data.message}
        // message.error(response.data.message);
      }
    } catch (error) {
      // message.error(error.response.data.message);
      return { success: false, errors: error.response.data.message };
    }
  },
  async sendSmtpDocument({ commit }, params) {
    try {
      // Make a POST request with paramsData
      const response = await apiClient.post('power/mail-price-document', params);
      if (response.data.status) {
        message.success(response.data.message);
        return response.data; // Return the data for further use
      }
    } catch (error) {
      return { success: false, errors: error.response.data.errors };
    }
  },
  async sendSmtpPdf({ commit }, params) {
    try {
      // Make a POST request with paramsData
      const response = await apiClient.post('power/contract/generate-and-send-pdf', params);
      if (response.data.success) {
        message.success(response.data.message);
        return response.data; // Return the data for further use
      }
    } catch (error) {
      return { success: false, errors: error.response.data.message };
    }
  },
  async fetchOrgLookupData({ commit }, paramsData) {
    try {
      // Make a POST request with paramsData
      let response = await apiClient.get(`/lookup/company-information/${paramsData.search_value}`);
      if (response.data.status) {
        return response.data?.data;
      }
    } catch (error) {
      message.error(error.response.data.message);
      console.error("Error fetching lookup", error); // Log any errors
    }
  },
};
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
